import React, { useCallback, useEffect, useState, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import HTMLReactParser, { domToReact } from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import * as yup from 'yup';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, RECORD } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { apiFetch, makePath } from 'functions.js';

// CORE COMPONENTS
import Form from 'core/form/Form';

// VARIABLES
const FIELD_VALIDATION = yup
  .string()
  .matches(/^[A-Za-z0-9]*$/, 'Must not contain any spaces or special characters.');

// FUNCTIONS
const searchValidation = message => yup.string().test('searchStockNo', parseLink(message), () => false);
const parseLink = el => {
  const replace = domNode => {
    const { name, attribs } = domNode;

    if (name === 'a' && (attribs?.id || []).includes('daily_sales_inventory_id')) {
      const dailySalesInventoryID = domNode.attribs.id.replace('daily_sales_inventory_id=', '');
      const label = domToReact(domNode.children, replace);
      return <a href={makePath(INTERFACE.inventory, RECORD.view, dailySalesInventoryID)}>{label}</a>;
    }
  };

  const santized = sanitizeHtml(el, {
    allowedAttributes: false,
    allowedSchemes: ['https'],
    disallowedTagsMode: 'escape'
  });
  return <span>{HTMLReactParser(santized, { replace })}</span>;
};

// MAIN COMPONENT
const StockNumber = ({
  name,
  readOnly,
  onSearch = () => {},
  ...props
}) => {

  // STATE
  const [ validationSchema, setValidationSchema ] = useState();

  // FORMIK CONTEXT
  const { validateField, errors, setFieldValue, values } = useFormikContext() || {};

  // MEMOS
  const stockNum = useMemo(
    () => cloneDeep(get(values, name)),
    [name, values]
  )

  // CALLBACKS
  const searchStockNo = useCallback(
    stock_num => {
      const errorMessage = onSearch(stock_num);
      if (!errorMessage) apiFetch({
        method: 'POST',
        endpoint: ENDPOINTS.inventory.stockNum,
        params: {
          stock_num
        },
        errorMessage: false,
        onError: ({ message }) => {
          setValidationSchema(searchValidation(message));
        }
      });
      else setValidationSchema(searchValidation(errorMessage));
    },
    [onSearch, setValidationSchema]
  );

  const handleChange = useCallback(
    () => {
      setValidationSchema(FIELD_VALIDATION);
    },
    [setValidationSchema]
  )

  const handleBlur = useCallback(
    e => {
      validateField(name);
      if (!readOnly && !errors?.[name] && !!e.target.value) searchStockNo(e.target.value);
    },
    [validateField, readOnly, errors, name, searchStockNo]
  )

  // EFFECTS
  useEffect(
    () => {
      if (!!validationSchema) validateField(name);
    },
    [name, validateField, validationSchema]
  )

  useEffect(() => {
    if (stockNum) {
      const capitalizedValue = stockNum.toUpperCase();
      setFieldValue(name, capitalizedValue)
    }
  }, [stockNum, setFieldValue, name]);

  // RENDER
  return (
    <Form.Control
      name={name}
      onChange={handleChange}
      onBlur={handleBlur}
      validationSchema={validationSchema}
      showFeedback={true}
      readOnly={readOnly}
      {...props}
    />
  );
};

// EXPORT
export default StockNumber;
